import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEOHead from '@/components/common/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, KeyRound, Shield } from 'lucide-react';

const API_BASE = '';

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [token, setToken] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changeError, setChangeError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(username, password);
      if (res.must_change_password) {
        setToken(res.token);
        setMustChange(true);
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    if (newPw.length < 8) { setChangeError('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setChangeError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: password, new_password: newPw }),
      });
      const data = await res.json();
      if (data.status === 'password_changed') {
        navigate('/admin/dashboard');
      } else {
        setChangeError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setChangeError('Failed to change password');
    }
    setLoading(false);
  };

  // Password change screen
  if (mustChange) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="password-change-page">
        <SEOHead title="Change Password" />
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
              Change Password
            </h1>
            <p className="text-sm text-amber-400 mt-2">You must change your password before continuing</p>
          </div>
          <form onSubmit={handleChangePassword} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
            {changeError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{changeError}</div>
            )}
            <div>
              <Label className="text-slate-400 text-sm">New Password</Label>
              <Input data-testid="new-password" type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required minLength={8} placeholder="Min 8 characters" />
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Confirm Password</Label>
              <Input data-testid="confirm-password" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required />
            </div>
            <Button data-testid="change-password-submit" type="submit" disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white">
              <KeyRound className="w-4 h-4 mr-2" />
              {loading ? 'Changing...' : 'Change Password & Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Normal login screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="admin-login-page">
      <SEOHead title="Admin Login" />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/oddsis-logo-dark.png" alt="ODDSIS" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
            Admin Panel
          </h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to manage the platform</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400" data-testid="login-error">{error}</div>
          )}
          <div>
            <Label className="text-slate-400 text-sm">Username</Label>
            <Input data-testid="login-username" value={username} onChange={e => setUsername(e.target.value)}
              className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required />
          </div>
          <div>
            <Label className="text-slate-400 text-sm">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
            <LogIn className="w-4 h-4 mr-2" />
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
